/**
 * Reclaiming device storage from media whose bytes are safely on the server.
 *
 * A photograph is 150 KB and a ten-second clip is around 1.25 MB — eight
 * times more — so a device that fills up in a month of photographs fills up
 * in four days of video. Nothing here ever deleted anything, because until
 * migration v6 nothing recorded that an upload had happened.
 *
 * Three rules shape this file, and each of them exists because the obvious
 * alternative loses a customer's evidence:
 *
 * **Never delete on upload success.** The timeline renders local media out of
 * OPFS (`useBlobUrl`), and `GET /media/{id}` needs a `Bearer` token, so the
 * server copy cannot simply go in a `src`. Deleting as soon as the bytes
 * landed would mean every capture the inspector took today shows an empty
 * frame the moment it syncs — on the device that recorded it, while they are
 * still standing on site. So reclamation is a *pressure* response, not a
 * completion hook: nothing is removed until the device is actually short of
 * room.
 *
 * **Measure the captures, not the origin.** `MediaStore.totalBytes()` reports
 * everything the origin holds — the SQLite database, IndexedDB, the service
 * worker's caches — and triggering on it would start deleting photographs to
 * make room for a database that is going to grow again anyway. The budget
 * here is over `SUM(byte_size)` of capture blobs, which is the only thing
 * this sweep can actually free.
 *
 * **A `failed` task still owns its bytes.** `failed` is not "abandoned", it is
 * "waiting for a person" — `retryTask()` puts it straight back — so treating
 * failed as settled would turn a recoverable upload failure into permanent
 * data loss. Everything except `succeeded` holds a reference.
 */
import { all, run } from '../db/sqlite';
import { platform } from '../platform';

/**
 * How much capture media the device may hold before the sweep starts.
 *
 * 512 MB is roughly 400 clips or 3,500 photographs — far more than one job's
 * worth, which is what matters: an inspector must never lose today's work to
 * a sweep, and by the time this trips they are hundreds of captures past it.
 */
export const MEDIA_BUDGET_BYTES = 512 * 1024 * 1024;

/**
 * Reclaim down to this much, once the budget trips.
 *
 * Deliberately well below the trigger rather than just under it. Freeing a
 * single file each time the ceiling is touched means running the sweep — and
 * paying its query — on almost every capture from then on; freeing down to
 * 60% means it runs rarely and does real work when it does.
 */
export const MEDIA_TARGET_BYTES = Math.floor(MEDIA_BUDGET_BYTES * 0.6);

/**
 * How long an uploaded capture is kept regardless of pressure.
 *
 * The capture → review → sign-off loop happens within one visit, and a blob
 * pulled out from under a reviewer mid-visit is the one failure mode that
 * would be visibly worse than a full disk. An hour covers that loop with room
 * to spare while still letting a heavy video day reclaim the same day.
 */
export const MIN_RECLAIM_AGE_MS = 60 * 60 * 1000;

export interface SweepResult {
  /** Bytes freed. Zero when the budget was not exceeded. */
  freedBytes: number;
  /** How many capture blobs were removed. */
  removed: number;
  /** Capture media held on the device after the sweep. */
  remainingBytes: number;
}

/** Capture media currently on the device, in bytes. */
export async function mediaBytesOnDevice(): Promise<number> {
  const rows = await all<{ total: number | null }>(
    `SELECT SUM(byte_size) AS total FROM captures WHERE blob_path IS NOT NULL`,
  );
  return rows[0]?.total ?? 0;
}

/**
 * Captures whose bytes may be reclaimed, oldest upload first.
 *
 * The two `NOT EXISTS` clauses are the reference guard, and they are separate
 * because a blob can be referenced two different ways. The first covers the
 * bytes directly: any unsettled task pointing at this `blob_path`, which is
 * the `media.upload` itself and any re-queued copy of it. The second covers
 * the capture: an unsettled media op for this capture id that has not yet
 * been given a path, or has been re-pointed. Either one means someone still
 * intends to send these bytes.
 *
 * Ordered by `uploaded_at` rather than `created_at` so the sweep frees what
 * has been safe on the server longest, which is also what the inspector is
 * least likely to be looking at.
 */
async function reclaimable(olderThan: string): Promise<
  Array<{ id: string; blob_path: string; byte_size: number }>
> {
  return all<{ id: string; blob_path: string; byte_size: number }>(
    `SELECT id, blob_path, byte_size
       FROM captures c
      WHERE c.blob_path IS NOT NULL
        AND c.media_id IS NOT NULL
        AND c.uploaded_at IS NOT NULL
        AND c.uploaded_at < ?
        AND c.deleted_at IS NULL
        AND NOT EXISTS (
              SELECT 1 FROM sync_tasks t
               WHERE t.blob_path = c.blob_path AND t.status <> 'succeeded')
        AND NOT EXISTS (
              SELECT 1 FROM sync_tasks t
               WHERE t.entity_id = c.id
                 AND t.op IN ('media.upload', 'signature.upload')
                 AND t.status <> 'succeeded')
      ORDER BY c.uploaded_at ASC`,
    [olderThan],
  );
}

/**
 * Free device storage if capture media is over budget.
 *
 * Safe to call often and cheap when there is nothing to do: over budget or
 * not is one indexed `SUM`, and everything else is skipped.
 *
 * `blob_path` is cleared in the same step as the delete, and in that order —
 * the file first, then the row. A crash between the two leaves a row claiming
 * a file that is gone, which `useBlobUrl` already handles (it renders the
 * empty state); the reverse leaves a file nothing points at, which nothing
 * would ever find again.
 */
export async function sweepUploadedMedia(
  budgetBytes = MEDIA_BUDGET_BYTES,
  targetBytes = MEDIA_TARGET_BYTES,
): Promise<SweepResult> {
  let held = await mediaBytesOnDevice();
  if (held <= budgetBytes) {
    return { freedBytes: 0, removed: 0, remainingBytes: held };
  }

  const cutoff = new Date(Date.now() - MIN_RECLAIM_AGE_MS).toISOString();
  const candidates = await reclaimable(cutoff);

  let freedBytes = 0;
  let removed = 0;
  for (const row of candidates) {
    if (held <= targetBytes) break;
    try {
      await platform().media.delete(row.blob_path);
    } catch {
      // A blob already gone is the good case, and a store that refuses to
      // delete is not a reason to abandon the rest of the sweep. Either way
      // the row below stops claiming it.
    }
    await run(`UPDATE captures SET blob_path = NULL WHERE id = ?`, [row.id]);
    held -= row.byte_size;
    freedBytes += row.byte_size;
    removed++;
  }

  return { freedBytes, removed, remainingBytes: held };
}
